import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { BorderoPdv } from '../../hooks/bordero'

const ACCENT = '#B0020B'

const Wrap = styled.div`
  position: relative;
`
const Selected = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #E0E0E0;
  background: #F9F9F9;
  border-radius: 11px;
  padding: 9px 12px;
  cursor: pointer;
  text-align: left;
  font-size: 13.5px;
  color: #101828;
  &:hover {
    border-color: #98a2b3;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
const Chevron = styled.span`
  margin-left: auto;
  color: #98a2b3;
`
const Panel = styled.div`
  position: absolute;
  z-index: 40;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #E0E0E0;
  border-radius: 12px;
  box-shadow: 0 12px 32px -12px rgba(16, 24, 40, 0.24);
  padding: 10px;
`
const Search = styled.input`
  width: 100%;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  margin-bottom: 8px;
`
const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
  button {
    border: none;
    background: transparent;
    color: ${ACCENT};
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 2px 4px;
  }
`
const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 240px;
  overflow-y: auto;
`
const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #101828;
  &:hover {
    background: #F9F9F9;
  }
  input {
    accent-color: ${ACCENT};
    cursor: pointer;
  }
`
const Hint = styled.div`
  font-size: 12px;
  color: #98a2b3;
  padding: 10px;
  text-align: center;
`

interface Props {
  options: BorderoPdv[]
  value: number[] // vazio = Todos
  onChange: (ids: number[]) => void
  loading?: boolean
  disabled?: boolean
}

const PdvPicker: React.FC<Props> = ({ options, value, onChange, loading, disabled }) => {
  const [open, setOpen] = useState(false)
  const [term, setTerm] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase()
    return q ? options.filter((o) => o.nome.toLowerCase().includes(q)) : options
  }, [options, term])

  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id])

  const label =
    value.length === 0 ? 'Todos' : `${value.length} PDV${value.length > 1 ? 's' : ''} selecionado${value.length > 1 ? 's' : ''}`

  return (
    <Wrap ref={wrapRef}>
      <Selected
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        {loading ? 'Carregando PDVs…' : label}
        <Chevron>⌄</Chevron>
      </Selected>
      {open && !disabled && (
        <Panel>
          <Search
            placeholder="Buscar PDV pelo nome"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <Actions>
            <button type="button" onClick={() => onChange([])}>
              Todos
            </button>
            {value.length > 0 && (
              <button type="button" onClick={() => onChange([])}>
                Limpar seleção
              </button>
            )}
          </Actions>
          {options.length === 0 ? (
            <Hint>Nenhum PDV com venda para a seleção.</Hint>
          ) : (
            <List>
              {filtered.map((o) => (
                <Row key={o.id} onClick={() => toggle(o.id)}>
                  <input type="checkbox" readOnly checked={value.includes(o.id)} />
                  {o.nome}
                </Row>
              ))}
              {filtered.length === 0 && <Hint>Nenhum PDV encontrado.</Hint>}
            </List>
          )}
        </Panel>
      )}
    </Wrap>
  )
}

export default PdvPicker
